import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Tabs,
  Tab,
  IconButton,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Dashboard as DashboardIcon,
  Receipt as ExpensesIcon,
  Paid as IncomeIcon,
  AccountBalanceWallet as AccountIcon,
  TrendingUp as ProjectionsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ExpenseManager from './components/ExpenseManager';
import IncomeManager from './components/IncomeManager';
import AccountManager from './components/AccountManager';
import ProjectionsView from './components/ProjectionsView';

// Create dark theme as default
const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#90caf9' : '#1976d2',
    },
    secondary: {
      main: mode === 'dark' ? '#f48fb1' : '#dc004e',
    },
    background: {
      default: mode === 'dark' ? '#121212' : '#f5f5f5',
      paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#000000',
      secondary: mode === 'dark' ? '#b3b3b3' : '#666666',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:600px)': {
        fontSize: '2.125rem',
      },
    },
    h5: {
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '8px',
          paddingRight: '8px',
          '@media (min-width: 600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-flexContainer': {
            '@media (max-width: 600px)': {
              justifyContent: 'space-around',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          '@media (max-width: 600px)': {
            fontSize: '0.75rem',
            padding: '6px 8px',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: 'calc(100vh - 200px)', // Ensure content area has minimum height
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Authenticated App Component
function AuthenticatedApp() {
  const [tabValue, setTabValue] = useState(0);
  const [darkMode, setDarkMode] = useState(true); // Dark mode as default
  const isMobile = useMediaQuery('(max-width:600px)');
  const { logout, user } = useAuth();
  
  const theme = createAppTheme(darkMode ? 'dark' : 'light');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = () => {
    logout();
  };

  const tabLabels = ['Dashboard', 'Expenses', 'Income', 'Accounts', 'Projections'];
  const tabIcons = [DashboardIcon, ExpensesIcon, IncomeIcon, AccountIcon, ProjectionsIcon];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <AccountBalanceIcon sx={{ mr: 2 }} />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 500,
            }}
          >
            Financial Tracker
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
            Welcome, {user?.username}
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={toggleDarkMode}
            sx={{ ml: 1 }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <Button 
            color="inherit" 
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ ml: 1 }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, sm: 3 },
          mb: isMobile ? 8 : 3, // Extra margin for mobile bottom navigation
          px: { xs: 1, sm: 3 },
        }}
      >
        {/* Desktop/Tablet Tabs */}
        {!isMobile && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="financial tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          <Dashboard />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <ExpenseManager />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <IncomeManager />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <AccountManager />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <ProjectionsView />
        </TabPanel>
      </Container>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            zIndex: 1000,
          }} 
          elevation={8}
        >
          <BottomNavigation
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            showLabels
            sx={{
              '& .MuiBottomNavigationAction-root': {
                fontSize: '0.6rem',
                minWidth: 'auto',
              },
            }}
          >
            {tabLabels.map((label, index) => {
              const IconComponent = tabIcons[index];
              return (
                <BottomNavigationAction
                  key={index}
                  label={label}
                  icon={<IconComponent fontSize="small" />}
                />
              );
            })}
          </BottomNavigation>
        </Paper>
      )}
    </ThemeProvider>
  );
}

// Main App Component with Authentication
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// App Content Component
function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <AuthenticatedApp /> : <Login />;
}

export default App;
